import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export const GRAFANA_ANNOTATION_METRICS_SELECTOR =
  'grafana/metrics-selector';
export const GRAFANA_ANNOTATION_SLO_LABEL_SELECTOR =
  'grafana/slo-label-selector';

export interface DashboardDetails {
  description?: string;
  folderTitle?: string;
  folderUrl?: string;
  panelCount: number;
  panels: Array<{ id?: number; title: string; type?: string }>;
  panelTypes: Record<string, number>;
  tags: string[];
  title: string;
  uid: string;
  updated?: string;
  updatedBy?: string;
  url: string;
  version?: number;
  datasources: string[];
}

export interface AlertRuleDetail {
  folder?: string;
  group?: string;
  labels: Record<string, string>;
  annotations: Record<string, string>;
  name: string;
  severity: string;
  uid?: string;
  url: string;
  state?: string;
}

export interface GrafanaSlo {
  uuid: string;
  name: string;
  description?: string;
  labels?: Array<{ key: string; value: string }>;
  objectives: Array<{ value: number; window: string }>;
  status?: {
    current?: number;
    remaining_error_budget?: number;
  };
}

export interface GrafanaAnnotation {
  id: number;
  time: number;
  timeEnd?: number;
  text: string;
  tags: string[];
  dashboardUID?: string;
  panelId?: number;
}

export interface MetricSeries {
  label: string;
  datapoints: Array<{ timestamp: number; value: number }>;
  latest?: number;
}

export interface RedMetrics {
  requestRate?: MetricSeries;
  errorRate?: MetricSeries;
  latency?: MetricSeries;
  datasourceUid?: string;
  datasourceName?: string;
}

type ProxyFetch = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
};

async function grafanaFetch<T>(
  { discoveryApi, fetchApi }: ProxyFetch,
  path: string,
): Promise<T> {
  const proxyBase = await discoveryApi.getBaseUrl('proxy');
  const response = await fetchApi.fetch(
    `${proxyBase}/grafana/api${path.startsWith('/') ? path : `/${path}`}`,
  );
  if (!response.ok) {
    throw new Error(
      `Grafana request failed (${response.status} ${response.statusText}) for ${path}`,
    );
  }
  return (await response.json()) as T;
}

export function parseLabelSelector(
  selector: string | string[] | undefined,
): Array<{ key: string; value: string }> {
  if (!selector) {
    return [];
  }
  const parts = Array.isArray(selector) ? selector : selector.split(',');
  return parts
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const idx = part.indexOf('=');
      if (idx <= 0) {
        return undefined;
      }
      return {
        key: part.slice(0, idx).trim(),
        value: part.slice(idx + 1).trim().replace(/^["']|["']$/g, ''),
      };
    })
    .filter((item): item is { key: string; value: string } => Boolean(item));
}

export function labelsMatch(
  labels: Record<string, string> | undefined,
  selectors: Array<{ key: string; value: string }>,
): boolean {
  if (!selectors.length) {
    return false;
  }
  if (!labels) {
    return false;
  }
  return selectors.every(({ key, value }) => labels[key] === value);
}

function collectPanels(
  panels: Array<Record<string, unknown>> | undefined,
): Array<{ id?: number; title: string; type?: string }> {
  if (!panels?.length) {
    return [];
  }
  const result: Array<{ id?: number; title: string; type?: string }> = [];
  for (const panel of panels) {
    const type = typeof panel.type === 'string' ? panel.type : undefined;
    const title =
      typeof panel.title === 'string' && panel.title.trim()
        ? panel.title
        : type === 'row'
          ? 'Row'
          : 'Untitled panel';
    if (type !== 'row') {
      result.push({
        id: typeof panel.id === 'number' ? panel.id : undefined,
        title,
        type,
      });
    }
    if (Array.isArray(panel.panels)) {
      result.push(
        ...collectPanels(panel.panels as Array<Record<string, unknown>>),
      );
    }
  }
  return result;
}

function collectDatasources(
  panels: Array<Record<string, unknown>> | undefined,
  found = new Set<string>(),
): string[] {
  if (!panels?.length) {
    return [...found];
  }
  for (const panel of panels) {
    const ds = panel.datasource;
    if (typeof ds === 'string' && ds) {
      found.add(ds);
    } else if (ds && typeof ds === 'object') {
      const uid = (ds as { uid?: string; type?: string }).uid;
      const type = (ds as { uid?: string; type?: string }).type;
      if (uid) {
        found.add(uid);
      } else if (type) {
        found.add(type);
      }
    }
    if (Array.isArray(panel.targets)) {
      for (const target of panel.targets as Array<Record<string, unknown>>) {
        const tds = target.datasource;
        if (typeof tds === 'string' && tds) {
          found.add(tds);
        } else if (tds && typeof tds === 'object') {
          const uid = (tds as { uid?: string }).uid;
          if (uid) {
            found.add(uid);
          }
        }
      }
    }
    if (Array.isArray(panel.panels)) {
      collectDatasources(panel.panels as Array<Record<string, unknown>>, found);
    }
  }
  return [...found];
}

export async function fetchDashboardDetails(
  apis: ProxyFetch,
  domain: string,
  uid: string,
): Promise<DashboardDetails> {
  const body = await grafanaFetch<{
    dashboard?: {
      description?: string;
      panels?: Array<Record<string, unknown>>;
      tags?: string[];
      title?: string;
      uid?: string;
      version?: number;
    };
    meta?: {
      folderTitle?: string;
      folderUrl?: string;
      updated?: string;
      updatedBy?: string;
      url?: string;
    };
  }>(apis, `/api/dashboards/uid/${encodeURIComponent(uid)}`);

  const panels = collectPanels(body.dashboard?.panels);
  const panelTypes = panels.reduce(
    (acc, panel) => {
      const key = panel.type || 'unknown';
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return {
    description: body.dashboard?.description,
    folderTitle: body.meta?.folderTitle,
    folderUrl: body.meta?.folderUrl
      ? `${domain}${body.meta.folderUrl}`
      : undefined,
    panelCount: panels.length,
    panels,
    panelTypes,
    tags: body.dashboard?.tags ?? [],
    title: body.dashboard?.title ?? uid,
    uid: body.dashboard?.uid ?? uid,
    updated: body.meta?.updated,
    updatedBy: body.meta?.updatedBy,
    url: `${domain}${body.meta?.url ?? `/d/${uid}`}`,
    version: body.dashboard?.version,
    datasources: collectDatasources(body.dashboard?.panels),
  };
}

export async function fetchAlertRules(
  apis: ProxyFetch,
  domain: string,
  selector: string | string[] | undefined,
): Promise<AlertRuleDetail[]> {
  const selectors = parseLabelSelector(selector);
  if (!selectors.length) {
    return [];
  }

  const response = await grafanaFetch<
    Record<
      string,
      Array<{
        name?: string;
        rules?: Array<{
          labels?: Record<string, string>;
          annotations?: Record<string, string>;
          grafana_alert?: { uid?: string; title?: string };
        }>;
      }>
    >
  >(apis, '/api/ruler/grafana/api/v1/rules');

  const rules: AlertRuleDetail[] = [];
  for (const [folder, groups] of Object.entries(response ?? {})) {
    for (const group of groups ?? []) {
      for (const rule of group.rules ?? []) {
        if (!labelsMatch(rule.labels, selectors)) {
          continue;
        }
        const uid = rule.grafana_alert?.uid;
        const name = rule.grafana_alert?.title ?? 'Untitled rule';
        rules.push({
          folder,
          group: group.name,
          labels: rule.labels ?? {},
          annotations: rule.annotations ?? {},
          name,
          severity: (rule.labels?.severity || 'unknown').toLowerCase(),
          uid,
          url: uid
            ? `${domain}/alerting/grafana/${uid}/view`
            : `${domain}/alerting/list`,
        });
      }
    }
  }
  return rules;
}

export async function fetchLiveAlerts(
  apis: ProxyFetch,
): Promise<
  Array<{
    labels: Record<string, string>;
    annotations: Record<string, string>;
    state: string;
    activeAt?: string;
    value?: string;
  }>
> {
  try {
    const body = await grafanaFetch<{
      data?: {
        alerts?: Array<{
          labels?: Record<string, string>;
          annotations?: Record<string, string>;
          state?: string;
          activeAt?: string;
          value?: string;
        }>;
      };
    }>(apis, '/api/prometheus/grafana/api/v1/alerts');
    return (body.data?.alerts ?? []).map(alert => ({
      labels: alert.labels ?? {},
      annotations: alert.annotations ?? {},
      state: alert.state ?? 'Unknown',
      activeAt: alert.activeAt,
      value: alert.value,
    }));
  } catch {
    return [];
  }
}

export async function fetchSlos(
  apis: ProxyFetch,
  selector: string | undefined,
): Promise<GrafanaSlo[]> {
  if (!selector) {
    return [];
  }
  try {
    const body = await grafanaFetch<
      | GrafanaSlo[]
      | { slos?: GrafanaSlo[]; data?: GrafanaSlo[] }
    >(apis, '/api/plugins/grafana-slo-app/resources/v1/slo?includeStatus=true');

    let slos: GrafanaSlo[] = [];
    if (Array.isArray(body)) {
      slos = body;
    } else if (Array.isArray(body.slos)) {
      slos = body.slos;
    } else if (Array.isArray(body.data)) {
      slos = body.data;
    }

    const selectors = parseLabelSelector(selector);
    if (!selectors.length) {
      return slos;
    }
    return slos.filter(slo => {
      const labelMap = Object.fromEntries(
        (slo.labels ?? []).map(label => [label.key, label.value]),
      );
      return labelsMatch(labelMap, selectors);
    });
  } catch {
    return [];
  }
}

export async function fetchAnnotations(
  apis: ProxyFetch,
  dashboardUids: string[],
): Promise<GrafanaAnnotation[]> {
  if (!dashboardUids.length) {
    return [];
  }
  const to = Date.now();
  const from = to - 7 * 24 * 60 * 60 * 1000;
  const results = await Promise.all(
    dashboardUids.slice(0, 5).map(async uid => {
      try {
        const items = await grafanaFetch<
          Array<{
            id: number;
            time: number;
            timeEnd?: number;
            text?: string;
            tags?: string[];
            dashboardUID?: string;
            panelId?: number;
          }>
        >(
          apis,
          `/api/annotations?dashboardUID=${encodeURIComponent(
            uid,
          )}&from=${from}&to=${to}&limit=50`,
        );
        return items.map(item => ({
          id: item.id,
          time: item.time,
          timeEnd: item.timeEnd,
          text: item.text ?? '',
          tags: item.tags ?? [],
          dashboardUID: item.dashboardUID ?? uid,
          panelId: item.panelId,
        }));
      } catch {
        return [] as GrafanaAnnotation[];
      }
    }),
  );
  return results
    .flat()
    .sort((a, b) => b.time - a.time)
    .slice(0, 40);
}

function parseRelativeSeconds(input: string, now = Math.floor(Date.now() / 1000)): number {
  if (input === 'now') {
    return now;
  }
  const match = input.match(/^now-(\d+)([smhd])$/);
  if (!match) {
    return now;
  }
  const value = Number(match[1]);
  const unit = match[2];
  const mult = unit === 's' ? 1 : unit === 'm' ? 60 : unit === 'h' ? 3600 : 86400;
  return now - value * mult;
}

async function resolvePrometheusUid(
  apis: ProxyFetch,
): Promise<{ uid: string; name: string } | undefined> {
  try {
    const datasources = await grafanaFetch<
      Array<{ uid?: string; name?: string; type?: string; isDefault?: boolean }>
    >(apis, '/api/datasources');
    const prometheus = datasources.filter(ds => ds.type === 'prometheus');
    const preferred =
      prometheus.find(ds => ds.isDefault) ||
      prometheus.find(ds =>
        /grafanacloud-.*-(prom|metrics)$/i.test(ds.name ?? ''),
      ) ||
      prometheus[0];
    if (!preferred?.uid) {
      return undefined;
    }
    return { uid: preferred.uid, name: preferred.name ?? preferred.uid };
  } catch {
    return undefined;
  }
}

async function queryRange(
  apis: ProxyFetch,
  datasourceUid: string,
  query: string,
  from: string,
  to: string,
  step: string,
): Promise<MetricSeries | undefined> {
  const start = parseRelativeSeconds(from);
  const end = parseRelativeSeconds(to);
  const params = new URLSearchParams({
    query,
    start: String(start),
    end: String(end),
    step,
  });
  try {
    const body = await grafanaFetch<{
      status?: string;
      data?: {
        result?: Array<{
          metric?: Record<string, string>;
          values?: Array<[number, string]>;
        }>;
      };
    }>(
      apis,
      `/api/datasources/proxy/uid/${encodeURIComponent(
        datasourceUid,
      )}/api/v1/query_range?${params}`,
    );
    const series = body.data?.result?.[0];
    if (!series?.values?.length) {
      return undefined;
    }
    const datapoints = series.values.map(([timestamp, value]) => ({
      timestamp: timestamp * 1000,
      value: Number(value),
    }));
    return {
      label: series.metric?.__name__ ?? 'series',
      datapoints,
      latest: datapoints[datapoints.length - 1]?.value,
    };
  } catch {
    return undefined;
  }
}

export async function fetchRedMetrics(
  apis: ProxyFetch,
  metricsSelector: string | undefined,
  timeFrom: string,
): Promise<RedMetrics | undefined> {
  if (!metricsSelector?.trim()) {
    return undefined;
  }

  const ds = await resolvePrometheusUid(apis);
  if (!ds) {
    return undefined;
  }

  const rateWindow =
    timeFrom === 'now-7d'
      ? '1h'
      : timeFrom === 'now-24h'
        ? '15m'
        : timeFrom === 'now-6h'
          ? '5m'
          : '5m';
  const step =
    timeFrom === 'now-7d'
      ? '2h'
      : timeFrom === 'now-24h'
        ? '5m'
        : timeFrom === 'now-6h'
          ? '1m'
          : '30s';

  // Prefer OTel spanmetrics (Gannett / common Grafana Cloud pattern).
  // metrics-selector is injected as PromQL label matchers, e.g. service_name="foo"
  const selector = metricsSelector.trim();
  const requestQuery = `sum(rate(traces_spanmetrics_calls_total{${selector},span_kind="SPAN_KIND_SERVER"}[${rateWindow}]))`;
  const errorQuery = `sum(rate(traces_spanmetrics_calls_total{${selector},span_kind="SPAN_KIND_SERVER",status_code="STATUS_CODE_ERROR"}[${rateWindow}])) / sum(rate(traces_spanmetrics_calls_total{${selector},span_kind="SPAN_KIND_SERVER"}[${rateWindow}]))`;
  const latencyQuery = `histogram_quantile(0.95, sum(rate(traces_spanmetrics_latency_bucket{${selector},span_kind="SPAN_KIND_SERVER"}[${rateWindow}])) by (le))`;

  const [requestRate, errorRate, latency] = await Promise.all([
    queryRange(apis, ds.uid, requestQuery, timeFrom, 'now', step),
    queryRange(apis, ds.uid, errorQuery, timeFrom, 'now', step),
    queryRange(apis, ds.uid, latencyQuery, timeFrom, 'now', step),
  ]);

  return {
    requestRate,
    errorRate,
    latency,
    datasourceUid: ds.uid,
    datasourceName: ds.name,
  };
}

export function exploreUrl(
  domain: string,
  metricsSelector?: string,
): string | undefined {
  if (!metricsSelector?.trim()) {
    return undefined;
  }
  const left = {
    datasource: 'prometheus',
    queries: [
      {
        refId: 'A',
        expr: `traces_spanmetrics_calls_total{${metricsSelector.trim()},span_kind="SPAN_KIND_SERVER"}`,
      },
    ],
    range: { from: 'now-1h', to: 'now' },
  };
  return `${domain}/explore?orgId=1&left=${encodeURIComponent(
    JSON.stringify(left),
  )}`;
}

export function healthFromAlerts(
  firing: number,
  pending: number,
): 'healthy' | 'degraded' | 'critical' {
  if (firing > 0) {
    return 'critical';
  }
  if (pending > 0) {
    return 'degraded';
  }
  return 'healthy';
}
